import { DateTime } from "luxon";

const memory = new Map<string, string>();
const keyFor = (organizationId: string, date: string) =>
  `live-shift-day:v2:${encodeURIComponent(organizationId)}:${date.slice(0, 10)}`;
const validDay = (value: unknown): value is string =>
  typeof value === "string" &&
  /^\d{4}-\d{2}-\d{2}$/.test(value) &&
  DateTime.fromISO(value).isValid;
const read = (key: string) => {
  try {
    return memory.get(key) ?? localStorage.getItem(key);
  } catch {
    return memory.get(key);
  }
};

/** Browser fallback until the API exposes the actual live-start timestamp.
 * Keep the observation day so legitimate early starts survive midnight, while
 * an unrelated old day cannot be injected into a newly observed shift.
 */
export function readLiveShiftDay(
  organizationId: string,
  date: string,
  now = DateTime.now(),
  inferredDay = now.toISODate()!,
): string | undefined {
  try {
    const record = JSON.parse(read(keyFor(organizationId, date)) ?? "null");
    const today = now.toISODate()!;
    if (
      !record ||
      !validDay(record.day) ||
      !validDay(record.observedOn) ||
      record.observedOn > today
    )
      return;
    const earliest = [
      date.slice(0, 10),
      record.observedOn,
      inferredDay,
    ].sort()[0];
    if (record.day < earliest || record.day > today) return;
    return record.day;
  } catch {
    return;
  }
}

export function writeLiveShiftDay(
  organizationId: string | null,
  date: string,
  day?: string,
) {
  if (!organizationId) return;
  const key = keyFor(organizationId, date);
  const existing = read(key);
  // Do not write on minute ticks, unrelated renders, or duplicate observations.
  if (
    day &&
    readLiveShiftDay(organizationId, date, DateTime.now(), day) === day
  )
    return;
  if (!day && !existing) return;
  const value = day
    ? JSON.stringify({ day, observedOn: DateTime.now().toISODate() })
    : undefined;
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
    memory.delete(key);
  } catch {
    if (value) memory.set(key, value);
    else memory.delete(key);
  }
}
